import React from 'react';
import i18n from 'src/utils/translate';
import styled, { css } from 'styled-components';
import * as log from 'loglevel';
import Router from 'next/router';

import SitebarDropdown from './sitebarDropdown';
import Badge from 'src/components/badges/badge';
import { trackFootfallEvent } from 'src/utils/footfall';
import footfallEventTypes from 'src/constants/footfallEventTypes';

import api from 'src/utils/api';
import config from 'config/config';
import { getCommunityLogoUrl } from 'src/utils/linkUtils';
import InformationCard from 'src/components/informationCard';
import { getDiscoverLinks } from 'src/modules/home/components/discoverSection';
import { text, textLight, textLinkSecondary } from 'styles-js/mixins/typography';
import { LinkButton } from 'styles-js/buttons';

const PROGRAM_URL = config.cdn.programsIndex;
const isServer = typeof window === 'undefined';

const ProgramEmptyStateSubtitle = styled.div`
  ${text};
  padding: 15px;
`;

const ProgramEmptyStateText = styled.div`
  ${textLight};
  padding-bottom: 10px;
`;

const SuggestedProgramList = styled.ul`
  list-style: none;
  padding-left: 0;
  margin: 0;
`;

const SuggestedProgramLinkItem = styled.a`
  padding: 7.5px 0!important;
  &:hover {
    background: none;
  }
`;

const Container = styled.div`
  .hub-menu__browse-communities__link {
    color: ${({theme}) => theme.colorBlue} !important;
  }
`;

const HubMenuButton = styled(LinkButton)`
  ${textLinkSecondary};
  font-family: ${({theme}) => theme.fontFamilyTitle};
  line-height: 49px;
  padding: 0 12px;
  display: flex;
  align-items: center;

  &:focus, &:hover, &:active {
    color: ${({theme}) => theme.colorBlack};
  }

  ${({isActive}) => isActive && css`
    color: ${({theme}) => theme.colorBlack};
  `};
`;

const Icon = styled.span`
  margin-right: 5px;
  font-size: ${({theme}) => theme.fontSizeM};
`;

const getFixedHeight = (communityCount, programsCount) => {
  if ((programsCount + communityCount >= 8) || programsCount === 0) return 530;
  // add back in === 0 and remove from above when scrollbar is always visible on dropdown
  // else if (programsCount === 0) {
  //   return (((programsList.length + communityCount + 1) * 43) + 200);
  else return 140 + ((communityCount + programsCount) * 43);
};

const getCurrentRoute = () => {
  if (isServer) return null;
  return Router.route;
};

const SuggestedProgramLink = ({ program }) => {
  const { name, id, params } = program;
  const href = `/${id}${params ? params : ''}`;
  const code = id.replace('programs/', '');
  return (
    <li>
      <SuggestedProgramLinkItem href={href} onClick={() => {
        trackFootfallEvent(footfallEventTypes.CLICKED, { clickMetadata: {section: 'program', element: name}, clickType: 'hub-menu' });
      }}>
        <img className="avatar avatar--xsmall" src={`${PROGRAM_URL}${code}.png`} />
        { name }
      </SuggestedProgramLinkItem>
    </li>
  );
};

const ProgramsEmptyState = () => {
  const programs = getDiscoverLinks().find(link => link.id === 'programs').links;
  return (
    <div>
      <ProgramEmptyStateSubtitle>{i18n.t("Looks like you're not enrolled in any programs yet!")}</ProgramEmptyStateSubtitle>
      <InformationCard>
        <ProgramEmptyStateText>{i18n.t('Browse programs')}</ProgramEmptyStateText>
        <SuggestedProgramList>
          { programs.map(program => <SuggestedProgramLink key={program.id} program={program} />) }
        </SuggestedProgramList>
      </InformationCard>
    </div>
  );
};


class HubMenu extends React.Component {
  constructor(props) {
    super(props);
    this.state = { showAllCommunities: false, showAllPrograms: false, programs: [] };
  }

  componentDidMount() {
    this.fetchPrograms();
  }

  fetchPrograms = async () => {
    try {
      const { data } = await api.get(`private/programs/subscribed?user-id=${this.props.userId}`);
      this.setState({ programs: data });
    } catch (ex) {
      log.error(ex);
    }
  }

  render() {
    const { handleClick, open, followedCommunities } = this.props;
    const { showAllCommunities, showAllPrograms, programs } = this.state;
    const sortedCommunities = followedCommunities.sort((a, b) => {
      if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
      if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
      return 0;
    });
    const COMMUNITIES_TO_SHOW = 5;
    const filteredCommunities = showAllCommunities ? sortedCommunities : sortedCommunities.slice(0, COMMUNITIES_TO_SHOW);
    const filteredPrograms = showAllPrograms ? programs : programs.slice(0, 3);

    return (
      <Container>
        <HubMenuButton
          isActive={open || getCurrentRoute() === '/myHub'}
          onClick={handleClick}
        >
          <Icon className="icon-communities" />
          {i18n.t('My hub')}
        </HubMenuButton>
        <SitebarDropdown fixedHeight={getFixedHeight(filteredCommunities.length, filteredPrograms.length)} open={open}>
          <div className="divider">
            {i18n.t('Communities')}
          </div>
          <ul data-test-id="hub-menu-community-list">
            {filteredCommunities.map(community => <li key={`hub-${community.slug}`}>
              <a href={`/${community.slug}`}>
                <img alt={community.name} className="avatar avatar--xsmall"
                  src={getCommunityLogoUrl(community.logoImageHash, true)} />
                {community.name}
              </a>
            </li>)}
            {((!showAllCommunities && followedCommunities.length <= 3) || showAllCommunities) && <li>
              <a
                className="hub-menu__browse-communities__link"
                href={'/search/communities'}
                onClick={() => {
                  trackFootfallEvent(footfallEventTypes.CLICKED, {
                    clickType: 'hub-menu',
                    clickMetadata: { section: 'community-search' },
                  });
                }}
              >
                <img alt="Find a community" className="avatar avatar--xsmall"
                  src={`${config.cdn.bg}BrowseCommunities.svg`} />
                {i18n.t('Find a community')}
              </a>
            </li>}
            {followedCommunities.length > 3 && <li>
              <a
                className="sitebar-dropdown__link"
                href="#"
                onClick={event => {
                  event.stopPropagation();
                  this.setState({ showAllCommunities: !showAllCommunities });
                }}
              >
                {showAllCommunities ? `- ${i18n.t('view less')}` : `+ ${i18n.t('view all your communities')}`}
              </a>
            </li>}
          </ul>
          <div className="divider">
            {i18n.t('Programs')}{' '}
            <Badge content={i18n.t('NEW')} type="new" />
          </div>
          {
            programs.length
              ?
                <ul>
                  {programs.map(program => <li key={program.name}>
                    <a href={`/programs/${program.code}`} onClick={() => {
                      const element = program.name;
                      trackFootfallEvent(footfallEventTypes.CLICKED, { clickMetadata: {section: 'programs', element}, clickType: 'hub-menu' });
                    }}>
                      <img alt={program.name} className="avatar avatar--xsmall"
                        src={`${PROGRAM_URL}${program.code}.png`} />
                      {program.name}
                    </a>
                  </li>)}
                  {programs.length > 3 && <li>
                    <a
                      className="sitebar-dropdown__link"
                      onClick={event => {
                        event.stopPropagation();
                        this.setState({ showAllPrograms: !showAllPrograms });
                      }}
                    >
                      {showAllPrograms ? `- ${i18n.t('view less')}` : `+ ${i18n.t('view all your programs')}`}
                    </a>
                  </li>}
                </ul>
              : <ProgramsEmptyState />
          }
        </SitebarDropdown>
      </Container>
    );
  }
}

export default HubMenu;
